/* App telemetry: what breaks in people's browsers, and which screens they
 * open (Blind Spots item 12).
 *
 * Before this, a crashed screen or a failed start reached only the browser's
 * console, so UI breakage reached us only when someone said so. This sends
 * five things to the platform's Application Insights (syncon-appinsights,
 * cloud role "daedalus-spa"):
 *   - errors: crashed screens and tiles (the error boundaries), a failed start
 *     or sign-in (main.tsx), and anything unhandled (the library's own
 *     window.onerror and unhandledrejection hooks);
 *   - screen views, one per route change, named by route with ids replaced
 *     (`/projects/:id?tab=financials`), for use by screen;
 *   - failed requests: a fetch that came back 5xx, 408 or 429, or never got an
 *     answer (status 0) and was not cancelled by the app. Every other request
 *     is dropped here, in the browser;
 *   - what the content security policy blocked, or would block in report-only
 *     mode (Blind Spots item 16): the rule, the blocked origin and the app's
 *     script file and line, so the policy can be enforced once nothing the
 *     app needs is reported;
 *   - a chat reply that did not arrive on its own connection (dynamic chat
 *     turns, Phase 3 PR 3.1): how the connection failed, what the recovery
 *     found (the reply, a turn that died, or still working), which chat, the
 *     seconds involved and the turn's correlation id. Phase 3's reconnect
 *     work is sized from it;
 *   - with each, the build it came from (VITE_BUILD_SHA) and the signed-in
 *     user's OID.
 *
 * What it never sends: what people type, answers, or data a screen shows.
 * Addresses lose their query and fragment (MSAL's #code=, search text, the
 * chat's ?t=) and their record ids (`/projects/:id`). An error message is
 * sent only when it has a known shape that is about code (a property read on
 * undefined, a failed chunk load, a React error number, a network failure);
 * any other message, such as the server's own error text, is replaced by a
 * fingerprint, so repeats can still be counted. No request or response body
 * is read. If the scrub itself fails, the item is dropped. Settings › Your
 * data and docs/faq.md say so.
 *
 * Off unless VITE_APPINSIGHTS_CONNECTION_STRING is set at build time: the
 * main-branch deploy sets it; PR previews, local and mock builds do not. The
 * library is a lazy chunk (telemetrySdk.ts) fetched alongside the app, so the
 * first paint never waits for it; anything reported before it arrives is
 * queued, up to 50. docs/SPA_TELEMETRY.md has the queries. */

const CONNECTION_STRING = (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING ?? '').trim();
const BUILD = (import.meta.env.VITE_BUILD_SHA ?? '').trim().slice(0, 12) || 'local';

/** The cloud role every item carries, as the services' AppRoleName does. */
export const TELEMETRY_ROLE = 'daedalus-spa';

const QUEUE_MAX = 50;
/** Longest text sent in any one field. */
const TEXT_MAX = 300;
/** Longest text the scrub looks at. Anything longer is cut first, so no
 *  pattern ever runs over the 32 KB a library message can reach. */
const SCAN_MAX = 1000;
/** Per page load, so a render loop cannot become a flood. */
const MAX_EXCEPTIONS = 30;
const MAX_FAILED_REQUESTS = 30;
const MAX_CSP_REPORTS = 20;

/** A custom event the app sends (Blind Spots item 16): something the
 *  content security policy blocked, or would block in report-only mode. */
export const CSP_EVENT = 'csp_violation';

/** A custom event the app sends (dynamic chat turns, Phase 3 PR 3.1): a chat
 *  reply that did not arrive on its own connection, and what the recovery
 *  poll found. */
export const CHAT_RECOVERY_EVENT = 'chat_recovery';

/** What the recovery poll found: the reply, a turn that died, a turn still
 *  running when the poll gave up, or the person moved to another chat. */
export const CHAT_RECOVERY_OUTCOMES = ['recovered', 'interrupted', 'still_working', 'left'] as const;
export type ChatRecoveryOutcome = (typeof CHAT_RECOVERY_OUTCOMES)[number];

/** How the send failed: a stream that went silent, was cut, ended without
 *  its terminal, sent an in-band error, or whose handler threw
 *  (api/streaming.ts StreamDeathKind); an HTTP error status; a fetch that
 *  got no answer at all; or an error the app's own code threw. */
export const SEND_FAILURES = [
  'stream_silent',
  'stream_cut',
  'stream_ended',
  'stream_error',
  'stream_handler',
  'http',
  'network',
  'client',
] as const;
export type SendFailure = (typeof SEND_FAILURES)[number];

/** The chats that run the recovery poll. */
export const CHAT_SURFACES = ['home', 'dashboard', 'meeting'] as const;
export type ChatSurface = (typeof CHAT_SURFACES)[number];

/** A turn's correlation id: HomeChat's random UUID, lower-case. Nothing
 *  else is sent: a UUID cannot carry words. (Its non-UUID fallback, for a
 *  browser without crypto.randomUUID, is left out.) */
const CORRELATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Per page load, as for errors. */
const MAX_CHAT_RECOVERIES = 20;
let chatRecoveries = 0;

/** What the library chunk hands back: the three calls the app makes itself. */
export interface TelemetrySink {
  trackException(error: Error, properties: Record<string, string>): void;
  trackPageView(name: string, uri: string): void;
  trackEvent(name: string, properties: Record<string, string>): void;
}

/** The parts of a library telemetry item the scrub reads and rewrites. */
export interface TelemetryItemLike {
  baseType?: string;
  baseData?: Record<string, unknown>;
  data?: Record<string, unknown>;
  tags?: Record<string, unknown>;
  /** Part A: `ext.trace.name` becomes the item's operation name. */
  ext?: Record<string, unknown>;
}

export interface ScrubContext {
  build: string;
  userId: string | null;
  counts: { exceptions: number; failedRequests: number };
}

let started = false;
let sink: TelemetrySink | null = null;
let queue: Array<(s: TelemetrySink) => void> = [];

function run(op: (s: TelemetrySink) => void, s: TelemetrySink): void {
  try {
    op(s);
  } catch {
    /* telemetry must never break the app */
  }
}

function enqueue(op: (s: TelemetrySink) => void): void {
  if (!started) return; // off, or the library failed to load: keep nothing
  if (sink) run(op, sink);
  else if (queue.length < QUEUE_MAX) queue.push(op);
}

/** Start telemetry, once, before anything else in main.tsx. `currentUserId`
 *  is read for every item, so a sign-in after start is picked up. */
export function initTelemetry(currentUserId: () => string | null): void {
  if (started || !CONNECTION_STRING) return;
  started = true;
  const counts = { exceptions: 0, failedRequests: 0 };
  const userId = () => {
    try {
      return currentUserId();
    } catch {
      return null;
    }
  };
  import('./telemetrySdk')
    .then(({ startAppInsights }) => {
      const s = startAppInsights(CONNECTION_STRING, (item) =>
        scrubItem(item, { build: BUILD, userId: userId(), counts }),
      );
      sink = s;
      const pending = queue;
      queue = [];
      for (const op of pending) run(op, s);
    })
    .catch(() => {
      started = false;
      queue = [];
    });
  if (typeof document !== 'undefined') {
    const seen = new Set<string>();
    document.addEventListener('securitypolicyviolation', (event) => {
      const props = cspReport(event);
      if (!props) return;
      const key = `${props.disposition} ${props.directive} ${props.blocked}`;
      if (seen.has(key) || seen.size >= MAX_CSP_REPORTS) return;
      seen.add(key);
      enqueue((s) => s.trackEvent(CSP_EVENT, props));
    });
  }
}

/** The parts of a `securitypolicyviolation` event that may be sent: which
 *  rule, the blocked origin (or `inline`, `eval`, a scheme such as `data:`,
 *  or `(none)`), the
 *  script file and line that tried, and whether it was blocked or only
 *  reported. Never the full blocked address or the script sample, which can
 *  carry data. Null for anything that does not look like a real report. */
export function cspReport(event: {
  effectiveDirective?: string;
  violatedDirective?: string;
  blockedURI?: string;
  sourceFile?: string;
  lineNumber?: number;
  disposition?: string;
}): Record<string, string> | null {
  const directive = (event.effectiveDirective || event.violatedDirective || '').split(' ')[0];
  if (!CSP_DIRECTIVE.test(directive)) return null;
  const raw = (event.blockedURI ?? '').slice(0, SCAN_MAX);
  const keyword = /^(inline|eval|wasm-eval|trusted-types-policy|trusted-types-sink)$/.test(raw) ? raw : '';
  // Browsers report a data:, blob: or file: address as the bare scheme
  // ("data"); a dot-free word is never an origin.
  const scheme = (/^(data|blob|about|javascript|filesystem):/i.exec(raw)?.[1] ?? /^[a-z][a-z0-9+-]*$/i.exec(raw)?.[0])?.toLowerCase();
  const blocked = keyword || (scheme ? `${scheme}:` : originOf(raw)) || '(none)';
  return {
    directive,
    blocked,
    source: cleanAddress(event.sourceFile ?? ''),
    line: String(event.lineNumber ?? 0),
    disposition: event.disposition === 'enforce' ? 'enforce' : 'report',
  };
}

const CSP_DIRECTIVE = /^[a-z-]{3,40}$/;
const CSP_BLOCKED = /^(inline|eval|wasm-eval|trusted-types-policy|trusted-types-sink|\(none\)|[a-z][a-z0-9+-]*:|https?:\/\/[a-z0-9.-]+(:\d+)?)$/i;

/** Report an error the app caught. `where` says which part caught it:
 *  scene, tile, markdown, chunk-load, boot or sign-in. */
export function reportError(error: unknown, where: string): void {
  const err = error instanceof Error ? error : new Error(`Non-Error thrown (${typeof error})`);
  enqueue((s) => s.trackException(err, { where }));
}

export interface ChatRecoveryReport {
  outcome: ChatRecoveryOutcome;
  failure: SendFailure;
  /** The HTTP status, when `failure` is `http`. */
  status?: number;
  surface: ChatSurface;
  /** From the send to its failure. */
  afterMs?: number;
  /** How long the recovery poll ran. */
  waitedMs: number;
  correlationId?: string;
}

/** A chat reply that did not arrive on its own connection
 *  (threadRecovery.recoverThreadTail calls it once per recovery). Only
 *  counts, seconds and the fixed words above are sent. */
export function reportChatRecovery(report: ChatRecoveryReport): void {
  if (chatRecoveries >= MAX_CHAT_RECOVERIES) return;
  chatRecoveries++;
  const seconds = (ms: number) => String(Math.max(0, Math.round(ms / 1000)));
  const props: Record<string, string> = {
    outcome: report.outcome,
    failure: report.failure,
    surface: report.surface,
    waited_s: seconds(report.waitedMs),
  };
  if (report.status !== undefined) props.status = String(report.status);
  if (report.afterMs !== undefined) props.after_s = seconds(report.afterMs);
  if (report.correlationId && CORRELATION_ID.test(report.correlationId)) {
    props.correlation = report.correlationId;
  }
  enqueue((s) => s.trackEvent(CHAT_RECOVERY_EVENT, props));
}

/** One screen view. App.tsx calls it on each route change. */
export function trackScreen(pathname: string, search: string): void {
  const name = screenName(pathname, search);
  const path = routePath(pathname);
  enqueue((s) => s.trackPageView(name, `${window.location.origin}${path}`));
}

// ---- Names and addresses -------------------------------------------------

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isId(segment: string): boolean {
  return /^\d+$/.test(segment) || GUID.test(segment) || (segment.length >= 16 && /\d/.test(segment));
}

/** A path with every record id replaced by `:id`. */
export function routePath(pathname: string): string {
  return (
    pathname
      .slice(0, SCAN_MAX)
      .split('/')
      .map((segment) => (isId(segment) ? ':id' : segment))
      .join('/') || '/'
  );
}

/** A route's name for counting: ids become `:id`, and the tab or view the
 *  screen shows rides along (`/projects/:id?tab=financials`). */
export function screenName(pathname: string, search = ''): string {
  const path = routePath(pathname);
  const params = new URLSearchParams(search.slice(0, SCAN_MAX));
  const shown = ['tab', 'view']
    .map((key) => [key, params.get(key) ?? ''] as const)
    .filter(([, value]) => /^[\w-]{1,32}$/.test(value))
    .map(([key, value]) => `${key}=${value}`);
  return shown.length ? `${path}?${shown.join('&')}` : path;
}

/** An address without its query or fragment. */
export function stripUrl(url: string): string {
  const cut = url.search(/[?#]/);
  return cut < 0 ? url : url.slice(0, cut);
}

/** An address as it may be sent: no query or fragment, and record ids
 *  replaced, except in the app's own build files (/assets/), whose hashed
 *  names say which chunk failed. */
export function cleanAddress(url: string): string {
  const bare = stripUrl(url.slice(0, SCAN_MAX));
  const m = /^([a-z][a-z0-9+.-]*:\/\/[^/]*)(.*)$/i.exec(bare);
  const origin = m ? m[1] : '';
  const path = m ? m[2] : bare;
  if (!path || path.startsWith('/assets/')) return origin + path;
  return origin + routePath(path);
}

/** Text with every query and fragment cut off the words that carry one,
 *  word by word, so it is linear however long the text. A word that starts
 *  with # or ? ("error #185") is not an address and stays. */
function stripQueries(text: string): string {
  return text.slice(0, SCAN_MAX).replace(/\S+/g, (word) => {
    const cut = word.search(/[?#]/);
    return cut > 0 ? word.slice(0, cut) : word;
  });
}

function cap(text: string): string {
  return text.length > TEXT_MAX ? `${text.slice(0, TEXT_MAX - 1)}…` : text;
}

// ---- Error messages ------------------------------------------------------

/** Error messages about the code, never about data, sent as they are with
 *  addresses cut. Each is matched against the message after its type
 *  prefixes ("Uncaught TypeError: "). A property name is kept only when it
 *  looks like code: a lower-case identifier. */
const SAFE_MESSAGES: readonly RegExp[] = [
  /^Cannot (?:read|set) properties of (?:undefined|null) \((?:reading|setting) '[a-z_$][\w$]{0,39}'\)$/,
  /^can't access property "[a-z_$][\w$]{0,39}", [\w$.]{1,80} is (?:undefined|null)$/,
  /^(?:undefined|null) is not an object \(evaluating '[\w$.]{1,120}'\)$/,
  /^[\w$.]{1,80} is not (?:a function|iterable|a constructor|an object|defined)$/,
  /^[\w$.]{1,80} is (?:undefined|null)$/,
  /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.|The network connection was lost\.)$/,
  /^(?:Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS)\S*(?:\s\S+){0,2}$/i,
  /^Minified React error #\d+/,
  /^ResizeObserver loop (?:limit exceeded|completed with undelivered notifications\.)$/,
  /^Script error\.?$/,
  /^(?:Maximum call stack size exceeded\.?|too much recursion)$/,
  /^Non-Error thrown \(\w+\)$/,
  /^(?:signal is aborted without reason|signal timed out|The operation was aborted\.|The user aborted a request\.)$/,
  // MSAL: "interaction_in_progress: …" keeps the error code only.
  /^[a-z]+(?:_[a-z]+)+(?=:)/,
];

const MESSAGE_PREFIX = /^(?:Uncaught |PromiseRejectionEvent: |Unhandled Promise Rejection: |(?:[A-Z][A-Za-z]*)?(?:Error|Exception): )/;

/** A short, stable stand-in for text that is not sent (FNV-1a, 32 bits). */
function fingerprint(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** An error message fit to send: its type prefixes and, when it has a safe
 *  shape, its text with addresses cut; otherwise a fingerprint in its place. */
export function safeMessage(message: string): string {
  let rest = message.slice(0, SCAN_MAX).trim();
  let prefix = '';
  for (let i = 0; i < 4; i++) {
    const m = MESSAGE_PREFIX.exec(rest);
    if (!m) break;
    prefix += m[0];
    rest = rest.slice(m[0].length);
  }
  for (const shape of SAFE_MESSAGES) {
    const m = shape.exec(rest);
    if (m) return cap(prefix + stripQueries(m[0]));
  }
  return cap(`${prefix}[message withheld ${fingerprint(rest)}]`);
}

const FRAME = /^\s*at\s|@\S+:\d+(?::\d+)?\)?\s*$/;
const CODE_NAME = /^[\w$.<>[\] ]{0,120}$/;

function cleanStack(stack: string): string {
  return stack
    .split('\n')
    .map((raw) => {
      const line = raw.slice(0, SCAN_MAX);
      if (!line.trim()) return line;
      return FRAME.test(line) ? stripQueries(line) : safeMessage(line);
    })
    .join('\n');
}

/** window.onerror@<address>:line:col, with the address cleaned. */
function cleanErrorSource(value: string): string {
  const m = /^([\w.]+@)(\S+?)(:\d+:\d+)?$/.exec(value.slice(0, SCAN_MAX));
  return m ? cap(m[1] + cleanAddress(m[2]) + (m[3] ?? '')) : safeMessage(value);
}

/** A chat_recovery property's allowed words (reportChatRecovery built
 *  them; checked again here). */
const CHAT_RECOVERY_WORDS: Readonly<Record<string, readonly string[]>> = {
  outcome: CHAT_RECOVERY_OUTCOMES,
  failure: SEND_FAILURES,
  surface: CHAT_SURFACES,
};

/** `event`: the custom event's name, or null for any other item. Only the
 *  csp_violation event's directive, blocked, source and disposition are a
 *  policy report, and only chat_recovery's outcome, failure, surface and
 *  correlation are its fixed words and id. */
function cleanProps(props: unknown, event: string | null): void {
  if (!props || typeof props !== 'object') return;
  const cspEvent = event === CSP_EVENT;
  const bag = props as Record<string, unknown>;
  for (const [key, value] of Object.entries(bag)) {
    if (typeof value !== 'string') continue;
    if (key === 'url') bag[key] = cap(cleanAddress(value));
    else if (key === 'refUri') bag[key] = originOf(value);
    else if (key === 'errorSrc') bag[key] = cleanErrorSource(value);
    else if (key === 'where' || key === 'typeName') bag[key] = cap(value);
    // A chat reply's recovery (reportChatRecovery built these; checked again).
    else if (event === CHAT_RECOVERY_EVENT && Object.prototype.hasOwnProperty.call(CHAT_RECOVERY_WORDS, key))
      bag[key] = CHAT_RECOVERY_WORDS[key].includes(value) ? value : '[withheld]';
    else if (event === CHAT_RECOVERY_EVENT && key === 'correlation')
      bag[key] = CORRELATION_ID.test(value) ? value : '[withheld]';
    // A content-security-policy report (cspReport built these; checked again).
    else if (cspEvent && key === 'directive') bag[key] = CSP_DIRECTIVE.test(value) ? value : '[withheld]';
    else if (cspEvent && key === 'disposition') bag[key] = value === 'enforce' ? 'enforce' : 'report';
    else if (cspEvent && key === 'blocked') bag[key] = CSP_BLOCKED.test(value) ? value : originOf(value) || '[withheld]';
    else if (cspEvent && key === 'source') bag[key] = cap(cleanAddress(value));
    // Line and column numbers arrive as strings; HttpMethod is the verb.
    else if (/^-?\d{1,12}$/.test(value) || (key === 'HttpMethod' && /^[A-Z]{3,7}$/.test(value))) continue;
    else bag[key] = safeMessage(value);
  }
}

// ---- Requests ------------------------------------------------------------

/** A request worth keeping: an error or throttle from the server, or no
 *  answer at all (status 0). A request the app cancelled never gets here:
 *  the library's dependency initializer drops it (wasCancelled). */
function isFailedRequest(base: Record<string, unknown>): boolean {
  if (base.success === true) return false;
  const code = Number(base.responseCode ?? base.resultCode ?? NaN);
  return code === 0 || code >= 500 || code === 408 || code === 429;
}

/** Whether a request was cancelled by the app (navigating away, a newer
 *  search), from the abort signal the library's dependency listener kept in
 *  the request's context. The library's own record of a rejected fetch does
 *  not keep the error, so the signal is the only way to tell a cancel from a
 *  failure. */
export function wasCancelled(context: unknown): boolean {
  const signal = (context as { signal?: { aborted?: boolean } } | null | undefined)?.signal;
  return Boolean(signal?.aborted);
}

/** "GET https://host/path?q" with the address cleaned. */
function cleanRequestName(name: string): string {
  const m = /^([A-Z]+) (\S+)$/.exec(name.slice(0, SCAN_MAX));
  return m ? `${m[1]} ${cleanAddress(m[2])}` : cleanAddress(name);
}

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

// ---- The scrub -----------------------------------------------------------

/** The telemetry initializer, run on every item before it is sent. Returns
 *  false to drop the item, and drops it if anything in here throws: the
 *  library would otherwise send the item as it was. */
export function scrubItem(item: TelemetryItemLike, ctx: ScrubContext): boolean {
  try {
    return scrub(item, ctx);
  } catch {
    return false;
  }
}

function scrub(item: TelemetryItemLike, ctx: ScrubContext): boolean {
  const base = item.baseData ?? {};
  switch (item.baseType) {
    case 'ExceptionData': {
      if (++ctx.counts.exceptions > MAX_EXCEPTIONS) return false;
      const details = Array.isArray(base.exceptions) ? (base.exceptions as Array<Record<string, unknown>>) : [];
      for (const ex of details) {
        if (typeof ex.message === 'string') ex.message = safeMessage(ex.message);
        if (typeof ex.stack === 'string') ex.stack = cleanStack(ex.stack);
        const frames = Array.isArray(ex.parsedStack) ? (ex.parsedStack as Array<Record<string, unknown>>) : [];
        for (const frame of frames) {
          if (typeof frame.method === 'string' && !CODE_NAME.test(frame.method)) frame.method = '[withheld]';
          if (typeof frame.fileName === 'string') frame.fileName = stripUrl(frame.fileName.slice(0, SCAN_MAX));
          if (typeof frame.assembly === 'string') frame.assembly = stripQueries(frame.assembly);
        }
      }
      break;
    }
    case 'RemoteDependencyData': {
      if (!isFailedRequest(base)) return false;
      if (++ctx.counts.failedRequests > MAX_FAILED_REQUESTS) return false;
      if (typeof base.name === 'string') base.name = cleanRequestName(base.name);
      if (typeof base.data === 'string') base.data = cleanAddress(base.data);
      if (typeof base.target === 'string') base.target = cleanAddress(base.target);
      break;
    }
    case 'PageviewData':
    case 'PageviewPerformanceData': {
      if (typeof base.uri === 'string') base.uri = cleanAddress(base.uri);
      if (typeof base.refUri === 'string') base.refUri = originOf(base.refUri);
      break;
    }
    case 'MessageData': {
      if (typeof base.message === 'string') base.message = safeMessage(base.message);
      break;
    }
    case 'EventData': {
      // The app sends two custom events; anything else is not ours to send.
      if (base.name !== CSP_EVENT && base.name !== CHAT_RECOVERY_EVENT) return false;
      break;
    }
    default:
      break;
  }
  const event = item.baseType === 'EventData' ? String(base.name) : null;
  cleanProps(base.properties, event);
  cleanProps(item.data, event);

  // tags is an array with named entries on real items; both take keys.
  const tags = (item.tags ??= {}) as Record<string, unknown>;
  tags['ai.cloud.role'] = TELEMETRY_ROLE;
  tags['ai.application.ver'] = ctx.build;
  if (ctx.userId) tags['ai.user.authUserId'] = ctx.userId;
  // The operation name is the page the item came from. The library takes it
  // from the address with its fragment, which on the sign-in landing holds
  // MSAL's #code=, and keeps it in ext.trace until the envelope is built.
  const operation = tags['ai.operation.name'];
  if (typeof operation === 'string') tags['ai.operation.name'] = routePath(stripUrl(operation));
  const trace = item.ext?.trace as Record<string, unknown> | undefined;
  if (trace && typeof trace.name === 'string') trace.name = routePath(stripUrl(trace.name));
  return true;
}
